import { useForm } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { z } from 'zod/v4'
import { ApiError } from '@/api/client'
import { resetPassword } from '@/api/resources/auth'
import { FormInput } from '@/components/forms/form-input'
import { Button } from '@/components/ui/button'
import { useServerErrors } from '@/hooks/use-server-errors'
import { useAuthStore } from '@/stores/auth-store'

const resetPasswordSearchSchema = z.object({
  token: z.string(),
  email: z.string(),
})

const resetPasswordSchema = z
  .object({
    password: z.string().min(8, 'Password must be at least 8 characters'),
    password_confirmation: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  })

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>

export const Route = createFileRoute('/_auth/reset-password')({
  head: () => ({ meta: [{ title: 'Reset Password - MyJunea' }] }),
  validateSearch: resetPasswordSearchSchema,
  component: ResetPasswordPage,
})

function ResetPasswordPage(): React.ReactElement {
  const navigate = useNavigate()
  const { token, email } = Route.useSearch()
  const { setAuth } = useAuthStore()
  const { serverErrors, handleMutationError, clearServerErrors } = useServerErrors()

  const mutation = useMutation({
    mutationFn: async (data: ResetPasswordFormValues) =>
      resetPassword({
        token,
        email,
        password: data.password,
        password_confirmation: data.password_confirmation,
      }),
    onSuccess: (response) => {
      clearServerErrors()
      setAuth(response.access_token, response.user, response.expires_at)
      void navigate({ to: '/' })
    },
    onError: handleMutationError,
  })

  const form = useForm({
    defaultValues: { password: '', password_confirmation: '' } as ResetPasswordFormValues,
    validators: {
      onSubmit: resetPasswordSchema,
    },
    onSubmit: ({ value }) => {
      mutation.mutate(value)
    },
  })

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <h1 className="text-primary text-3xl leading-10 font-extrabold tracking-tight">
          Reset password
        </h1>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          clearServerErrors()
          mutation.reset()
          void form.handleSubmit()
        }}
        className="flex flex-col gap-6"
      >
        <div className="flex flex-col gap-2.5">
          <form.Field name="password">
            {(field) => (
              <FormInput
                field={field}
                label=""
                type="password"
                placeholder="New password"
                autoComplete="new-password"
                serverErrors={serverErrors}
              />
            )}
          </form.Field>

          <form.Field name="password_confirmation">
            {(field) => (
              <FormInput
                field={field}
                label=""
                type="password"
                placeholder="Confirm new password"
                autoComplete="new-password"
                serverErrors={serverErrors}
              />
            )}
          </form.Field>
        </div>

        {mutation.error instanceof ApiError && mutation.error.status !== 422 && (
          <p className="text-destructive text-sm">
            {mutation.error.body.message}{' '}
            <Link to="/forgot-password" className="underline">
              Request a new link.
            </Link>
          </p>
        )}

        <Button
          type="submit"
          className="h-12 w-full rounded-full text-base font-semibold"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Resetting...' : 'Reset password'}
          {!mutation.isPending && <ArrowRight className="ml-1 size-5" />}
        </Button>
      </form>
    </div>
  )
}
